import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { elevenLabsFetch } from "@/lib/elevenlabs.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getVideoPreset } from "@/lib/video-presets";


const BUCKET = "radio-media";

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

async function signUrl(path: string, ttl = 60 * 60 * 6) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.storage
    .from(BUCKET)
    .createSignedUrl(path, ttl);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

function pathFromUrl(maybeUrl: string | null | undefined): string | null {
  if (!maybeUrl) return null;
  // We always store storage paths (e.g. "episodes/abc.mp3") in the DB.
  if (maybeUrl.startsWith("http")) return null;
  return maybeUrl;
}

// PUBLIC — latest published episode + active tracks, with playable URLs.
export const getRadioFeed = createServerFn({ method: "GET" }).handler(
  async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: episodes }, { data: tracks }] = await Promise.all([
      supabaseAdmin
        .from("radio_episodes")
        .select("id, slug, title, topic, script, audio_url, duration_sec, published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(20),
      supabaseAdmin
        .from("radio_tracks")
        .select("id, title, artist, audio_url, duration_sec, sort_order, media_kind, mime_type, thumbnail_url")
        .eq("active", true)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true })
        .limit(100),
    ]);

    const signedEpisodes = await Promise.all(
      (episodes ?? []).map(async (e) => ({
        ...e,
        audio_signed_url: await signUrl(pathFromUrl(e.audio_url) ?? ""),
      })),
    );
    const signedTracks = await Promise.all(
      (tracks ?? []).map(async (t) => ({
        ...t,
        audio_signed_url: await signUrl(pathFromUrl(t.audio_url) ?? ""),
        thumbnail_signed_url: t.thumbnail_url
          ? await signUrl(pathFromUrl(t.thumbnail_url) ?? "", 60 * 60 * 24)
          : null,
      })),
    );
    return { episodes: signedEpisodes, tracks: signedTracks };
  },
);


// PUBLIC — single published episode by slug (or uuid fallback), with signed audio URL.
export const getRadioEpisode = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(300) }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        data.slug,
      );
    const q = supabaseAdmin
      .from("radio_episodes")
      .select("id, slug, title, topic, script, audio_url, duration_sec, published_at, status")
      .eq("status", "published");
    const { data: ep, error } = await (isUuid
      ? q.eq("id", data.slug)
      : q.eq("slug", data.slug)
    ).maybeSingle();
    if (error) throw new Error(error.message);
    if (!ep) return { episode: null as null };
    const audio_signed_url = await signUrl(pathFromUrl(ep.audio_url) ?? "");
    return { episode: { ...ep, audio_signed_url } };
  });

// ADMIN — list all episodes/tracks (drafts included)
export const adminListRadio = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: episodes }, { data: tracks }] = await Promise.all([
      supabaseAdmin
        .from("radio_episodes")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200),
      supabaseAdmin
        .from("radio_tracks")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    const eps = await Promise.all(
      (episodes ?? []).map(async (e) => ({
        ...e,
        audio_signed_url: await signUrl(pathFromUrl(e.audio_url) ?? ""),
      })),
    );
    const trk = await Promise.all(
      (tracks ?? []).map(async (t) => ({
        ...t,
        audio_signed_url: await signUrl(pathFromUrl(t.audio_url) ?? ""),
        thumbnail_signed_url: t.thumbnail_url
          ? await signUrl(pathFromUrl(t.thumbnail_url) ?? "", 60 * 60 * 24)
          : null,
      })),
    );
    return { episodes: eps, tracks: trk };
  });


// ADMIN — generate a new episode (AI script + optional ElevenLabs TTS).
export const adminGenerateEpisode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        topic: z.string().trim().min(4).max(400),
        title: z.string().trim().max(200).optional(),
        voiceId: z.string().trim().max(80).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);

    const lovableKey = process.env.LOVABLE_API_KEY;
    if (!lovableKey) throw new Error("AI not configured");

    const systemPrompt = `You are the lead presenter of "NDL Justice Radio" — a South African talk-radio show run by Nkadimeng Danny Legal & Compliance Support.

WRITE AS A 4–6 MINUTE SOLO RADIO MONOLOGUE in plain South African English. The script will be read aloud by a text-to-speech voice, so:
- Write the way people SPEAK, not the way lawyers write. Short sentences. Contractions. Natural pauses (use commas and full stops, not stage directions).
- No headings, no bullet points, no markdown — just spoken paragraphs.
- Start with a warm greeting: "You're tuned in to NDL Justice Radio, I'm your host..." Pick a credible South African presenter name.
- Discuss a REAL, PRESSING issue facing ordinary South Africans (the topic given). Be concrete: name the law in plain words, give an example of a person facing this, and give 2–3 PRACTICAL solutions / steps they can take TODAY.
- Mention free help where it exists: Legal Aid SA (0800 110 110), the CCMA, the SAPS, the Small Claims Court, the Public Protector — whichever fits.
- Add one short story or scenario to keep it human.
- Close by inviting listeners to visit nkadimengdannylcs.com for free templates and to book a paid consultation for tailored help.
- Do NOT promise specific legal outcomes. Make clear this is general information, not legal advice.
- 700–900 words. No stage directions. No "[pause]". Just clean spoken paragraphs.`;

    const aiRes = await fetch(
      "https://ai.gateway.lovable.dev/v1/chat/completions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Lovable-API-Key": lovableKey,
        },
        body: JSON.stringify({
          model: "google/gemini-2.5-pro",
          messages: [
            { role: "system", content: systemPrompt },
            {
              role: "user",
              content: `Topic for today's episode: ${data.topic}\n\nWrite the full spoken script now.`,
            },
          ],
        }),
      },
    );
    if (!aiRes.ok) {
      const t = await aiRes.text();
      throw new Error(`AI script generation failed: ${aiRes.status} ${t.slice(0, 200)}`);
    }
    const aiJson = (await aiRes.json()) as {
      choices?: { message?: { content?: string } }[];
    };
    const script = (aiJson.choices?.[0]?.message?.content ?? "").trim();
    if (!script) throw new Error("Empty script returned by AI");

    const title =
      data.title?.trim() ||
      `NDL Justice Radio — ${data.topic.slice(0, 80)}`;

    // Optional TTS — only if ElevenLabs key is configured.
    let audioPath: string | null = null;
    let durationSec: number | null = null;
    const ttsKey = process.env.ELEVENLABS_API_KEY;
    let ttsNote = "TTS skipped: ELEVENLABS_API_KEY not set. Episode saved as text-only.";

    if (ttsKey) {
      try {
        const voiceId = data.voiceId || "JBFqnCBsd6RMkjVDRZzb"; // George — warm male SA-friendly
        // ElevenLabs single request limit is generous; keep under 4500 chars.
        const ttsText = script.length > 4500 ? script.slice(0, 4500) : script;
        const ttsRes = await elevenLabsFetch(
          `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}?output_format=mp3_44100_128`,
          {
            method: "POST",
            headers: {
              "xi-api-key": ttsKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: ttsText,
              model_id: "eleven_multilingual_v2",
              voice_settings: {
                stability: 0.5,
                similarity_boost: 0.75,
                style: 0.35,
                use_speaker_boost: true,
              },
            }),
          },
        ).catch((e: unknown) => e as Error);
        if (ttsRes instanceof Error) {
          ttsNote = `TTS failed: ${ttsRes.message}`;
        } else if (!ttsRes.ok) {
          const t = await ttsRes.text();
          ttsNote = `TTS failed (${ttsRes.status}): ${t.slice(0, 200)}`;
        } else {
          const buf = new Uint8Array(await ttsRes.arrayBuffer());
          const { supabaseAdmin } = await import(
            "@/integrations/supabase/client.server"
          );
          const fname = `episodes/${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}.mp3`;
          const { error: upErr } = await supabaseAdmin.storage
            .from(BUCKET)
            .upload(fname, buf, {
              contentType: "audio/mpeg",
              upsert: false,
            });
          if (upErr) {
            ttsNote = `Upload failed: ${upErr.message}`;
          } else {
            audioPath = fname;
            // rough estimate: 150 wpm at TTS pace
            durationSec = Math.round((ttsText.split(/\s+/).length / 150) * 60);
            ttsNote = "Voice generated and uploaded.";
          }
        }
      } catch (e) {
        ttsNote = `TTS error: ${
          e instanceof Error ? e.message : String(e)
        }`;
      }
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("radio_episodes")
      .insert({
        title,
        topic: data.topic,
        script,
        audio_url: audioPath,
        duration_sec: durationSec,
        status: "draft",
        created_by: context.userId,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);

    return { episode: row, ttsNote };
  });

// ADMIN — change episode status (publish / archive / draft)
export const adminSetEpisodeStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["draft", "published", "archived"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("radio_episodes")
      .update({
        status: data.status,
        published_at: data.status === "published" ? new Date().toISOString() : null,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminDeleteEpisode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("radio_episodes")
      .select("audio_url")
      .eq("id", data.id)
      .maybeSingle();
    if (row?.audio_url) {
      await supabaseAdmin.storage.from(BUCKET).remove([row.audio_url]).catch(() => {});
    }
    const { error } = await supabaseAdmin
      .from("radio_episodes")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ADMIN — record a track that's already been uploaded to the bucket by the client.
export const adminCreateTrack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        title: z.string().trim().min(1).max(200),
        artist: z.string().trim().max(200).optional().default(""),
        path: z.string().trim().min(1).max(400),
        durationSec: z.number().int().positive().max(36000).optional(),
        sortOrder: z.number().int().min(0).max(10000).optional().default(0),
        mediaKind: z.enum(["audio", "video"]).optional().default("audio"),
        mimeType: z.string().trim().max(120).optional(),
        thumbnailPath: z.string().trim().min(1).max(400).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("radio_tracks")
      .insert({
        title: data.title,
        artist: data.artist,
        audio_url: data.path,
        duration_sec: data.durationSec ?? null,
        sort_order: data.sortOrder ?? 0,
        active: true,
        created_by: context.userId,
        media_kind: data.mediaKind ?? "audio",
        mime_type: data.mimeType ?? null,
        thumbnail_url: data.thumbnailPath ?? null,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { track: row };
  });


export const adminToggleTrack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ id: z.string().uuid(), active: z.boolean() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("radio_tracks")
      .update({ active: data.active })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminDeleteTrack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("radio_tracks")
      .select("audio_url, thumbnail_url")
      .eq("id", data.id)
      .maybeSingle();
    const toRemove = [row?.audio_url, row?.thumbnail_url].filter(
      (p): p is string => !!p && !p.startsWith("http"),
    );
    if (toRemove.length) {
      await supabaseAdmin.storage.from(BUCKET).remove(toRemove).catch(() => {});
    }

    const { error } = await supabaseAdmin
      .from("radio_tracks")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ADMIN — synthesize one short story dialogue line for the in-browser video
// generator. Uses the Lovable AI Gateway TTS (OpenAI gpt-4o-mini-tts) so no
// per-character ElevenLabs key is required. Returns base64-encoded mp3 audio
// so the client can decode it into an AudioBuffer and route it through the
// same AudioContext destination as music / SFX — this is the ONLY way the
// spoken characters end up inside the recorded webm (speechSynthesis output
// cannot be captured by MediaRecorder).
const STORY_VOICE_PRESETS: Record<
  string,
  { voice: string; instructions: string; speed: number }
> = {
  narrator: {
    voice: "onyx",
    instructions: "Warm, authoritative South African radio narrator. Steady pace, clear diction, slight gravitas.",
    speed: 0.98,
  },
  danny: {
    voice: "ash",
    instructions: "Confident male South African legal advisor in his 30s. Friendly but firm, measured pace, calmly reassuring.",
    speed: 1.0,
  },
  client: {
    voice: "nova",
    instructions: "Female client, mid 20s, slightly anxious and hopeful. Warm, expressive, a touch faster.",
    speed: 1.05,
  },
  judge: {
    voice: "echo",
    instructions: "Older male judge. Deep, slow, solemn, commanding courtroom presence.",
    speed: 0.85,
  },
  worker: {
    voice: "verse",
    instructions: "Working-class South African man, frustrated but earnest. Plain spoken, slightly raised energy.",
    speed: 1.08,
  },
  boss: {
    voice: "fable",
    instructions: "Older male business owner. Gruff, impatient, dismissive but not shouting.",
    speed: 0.95,
  },
};

export const adminSynthesizeStoryTurn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        character: z.string().trim().min(1).max(40),
        text: z.string().trim().min(1).max(800),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const lovableKey = process.env.LOVABLE_API_KEY;
    if (!lovableKey) throw new Error("AI not configured (LOVABLE_API_KEY missing).");

    const preset =
      STORY_VOICE_PRESETS[data.character.toLowerCase()] ??
      STORY_VOICE_PRESETS.narrator;

    const res = await fetch("https://ai.gateway.lovable.dev/v1/audio/speech", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Lovable-API-Key": lovableKey,
      },
      body: JSON.stringify({
        model: "openai/gpt-4o-mini-tts",
        input: data.text,
        voice: preset.voice,
        instructions: preset.instructions,
        speed: preset.speed,
        response_format: "mp3",
      }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      if (res.status === 429) throw new Error("Voice rate limit hit, please retry in a moment.");
      if (res.status === 402) throw new Error("Workspace credits exhausted — top up to continue generating voice.");
      throw new Error(`TTS failed (${res.status}): ${t.slice(0, 200)}`);
    }
    const buf = new Uint8Array(await res.arrayBuffer());
    let bin = "";
    const chunk = 0x8000;
    for (let i = 0; i < buf.length; i += chunk) {
      bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + chunk)));
    }
    const base64 = btoa(bin);
    return { mime: "audio/mpeg", base64 };
  });

// ADMIN — generate a photorealistic scene background image for a Story Radio
// video scene. Each request produces a unique 16:9 image tailored to the
// venue, scene title and cast so every scene in a story feels like a real
// location rather than the flat gradient fallback. Uses Gemini image via the
// Lovable AI Gateway; returned as base64 PNG the client draws onto the canvas.
const SCENE_VENUE_PROMPTS: Record<string, string> = {
  courtroom:
    "the interior of a South African court, judge's bench, wooden panelling, national flag, warm overhead lighting, wide establishing angle, no visible people",
  office:
    "a modern professional South African law firm office, glass partitions, city skyline through the window, warm dusk light, no visible people",
  workshop:
    "a small industrial workshop / construction yard in South Africa, tools, safety signage, natural daylight, no visible people",
  home:
    "a modest, tidy South African family home living room, warm lamp light, family photos on the wall, no visible people",
  street:
    "a Johannesburg street scene at golden hour, minibus taxis in the distance, storefronts, no visible people",
  studio:
    "a warm, professional radio studio with microphones, acoustic panels, deep navy walls with gold accents, no visible people",
};

export const adminGenerateSceneBackground = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        venue: z
          .enum(["courtroom", "office", "workshop", "home", "street", "studio"])
          .default("studio"),
        title: z.string().trim().max(200).default(""),
        mood: z.string().trim().max(120).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const lovableKey = process.env.LOVABLE_API_KEY;
    if (!lovableKey) throw new Error("AI not configured (LOVABLE_API_KEY missing).");

    const venuePrompt =
      SCENE_VENUE_PROMPTS[data.venue] ?? SCENE_VENUE_PROMPTS.studio;
    const prompt = [
      "Photorealistic cinematic wide establishing shot, 16:9 aspect ratio (1280x720).",
      `Setting: ${venuePrompt}.`,
      data.title ? `Scene context: ${data.title}.` : "",
      data.mood ? `Mood: ${data.mood}.` : "Mood: warm, editorial, professional.",
      "Shallow depth of field, soft cinematic lighting, high production value.",
      "IMPORTANT: no visible people, no faces, no text overlays, no logos, no watermarks.",
      "Leave the lower two-thirds of the frame visually calm so cartoon characters can be composited over it.",
    ]
      .filter(Boolean)
      .join(" ");

    const res = await fetch(
      "https://ai.gateway.lovable.dev/v1/images/generations",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Lovable-API-Key": lovableKey,
        },
        body: JSON.stringify({
          model: "google/gemini-3.1-flash-image",
          messages: [{ role: "user", content: prompt }],
          modalities: ["image", "text"],
        }),
      },
    );
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      if (res.status === 429)
        throw new Error("Image rate limit hit, please retry shortly.");
      if (res.status === 402)
        throw new Error(
          "Workspace credits exhausted — top up to keep generating scene backgrounds.",
        );
      throw new Error(`Background image failed (${res.status}): ${t.slice(0, 200)}`);
    }
    const json = (await res.json()) as {
      data?: { b64_json?: string }[];
    };
    const b64 = json.data?.[0]?.b64_json;
    if (!b64) throw new Error("Background image response missing b64_json.");
    return { mime: "image/png", base64: b64 };
  });



// ADMIN — generate a STORY SCRIPT (not an episode) in the exact format the
// in-browser RadioTextToVideo parser expects. The output is plain text the
// user can review/edit BEFORE turning it into a video.
//
// Format contract enforced by the system prompt:
//   First line:  "<Scene title> @<primary> #<id>"
//   Body lines:  "@<character>: <dialogue>"  |  ">Choice text -> targetId"
//                "!sfx=<key>"  |  "!ambience=<venue>"  |  "!venue=<venue>"
//   Scene break: "---" on its own line.
//
// Built-in character keys: narrator, danny, client, judge, worker, boss.
// Users may pass extra custom characters (label + role hint); they are mapped
// onto the closest built-in voice slot so the existing renderer/TTS works
// without schema changes downstream.
export const adminGenerateStoryScript = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        topic: z.string().trim().min(4).max(600),
        tone: z
          .enum(["educational", "dramatic", "comedic", "investigative", "uplifting"])
          .optional()
          .default("educational"),
        sceneCount: z.number().int().min(2).max(12).optional().default(5),
        targetMinutes: z.number().min(0.25).max(20).optional().default(4),
        lengthId: z.enum(["30s", "60s", "90s"]).optional(),

        characters: z
          .array(
            z.object({
              key: z.string().trim().min(1).max(20),
              label: z.string().trim().min(1).max(40),
              role: z.string().trim().max(160).optional().default(""),
              voiceSlot: z
                .enum(["narrator", "danny", "client", "judge", "worker", "boss"])
                .optional(),
            }),
          )
          .min(2)
          .max(8),
        branching: z.boolean().optional().default(true),
        notes: z.string().trim().max(800).optional().default(""),
        domain: z.enum(["services", "llb"]).optional().default("services"),
        moduleCode: z.string().trim().max(20).optional(),
        moduleTitle: z.string().trim().max(160).optional(),
        moduleNotes: z.string().trim().max(800).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const lovableKey = process.env.LOVABLE_API_KEY;
    if (!lovableKey) throw new Error("AI not configured (LOVABLE_API_KEY missing).");

    // Shared length engine — the chosen preset drives runtime, scene count and
    // the spoken word budget so the episode is always COMPLETE within length.
    const preset = data.lengthId ? getVideoPreset(data.lengthId) : null;
    const targetSeconds = preset ? preset.seconds : Math.round(data.targetMinutes * 60);
    const sceneTarget = preset ? preset.scenes : data.sceneCount;
    const wordBudget = preset ? preset.words : Math.round(data.targetMinutes * 140);
    const perScene = Math.max(18, Math.round(wordBudget / sceneTarget));



    const castLines = data.characters
      .map((c) => {
        const slot = c.voiceSlot ?? "narrator";
        return `- @${slot} (display label: "${c.label}", input key alias: "${c.key}")${c.role ? ` — ${c.role}` : ""}`;
      })
      .join("\n");

    // Build a custom-key → voice-slot map for defensive post-processing.
    const keyToSlot = new Map<string, string>();
    for (const c of data.characters) {
      keyToSlot.set(c.key.trim().toLowerCase(), (c.voiceSlot ?? "narrator").toLowerCase());
    }

    const isLlb = data.domain === "llb";
    const scopeBlock = isLlb
      ? `SCOPE — this is an NDLCS Education / UNISA LLB story episode. The plot, examples, and dialogue MUST be derived strictly from the syllabus and concepts of this module:
- Module: ${data.moduleCode ?? "(unspecified)"} — ${data.moduleTitle ?? "(unspecified)"}
${data.moduleNotes ? `- Key syllabus pointers: ${data.moduleNotes}` : ""}
The episode must teach a real concept from this module through the story (apply a rule to facts, work through an IRAC scenario, or dramatise a case). Do NOT invent facts that contradict South African law.

BRAND-SOLUTION ENDING — non-negotiable:
- The FINAL scene (id "end") MUST show how NDLCS LLB tutoring helps the student master this module.
- It must invite the student to enrol in NDLCS UNISA LLB tutoring (R150/module, 10% off for 4+ modules) and include a clear CTA (visit nkadimengdannylcs.com or WhatsApp 071 276 6198).
- The narrator delivers the closing CTA line.`
      : `SCOPE — every story MUST stay strictly within NDLCS's service lines and be DERIVED from a real client situation in one of these services:
- South African labour law & CCMA matters (unfair dismissal, retrenchment, condonation, Form 7.11)
- Employment contracts, workplace policies, disciplinary processes
- Company secretarial services (MOI, registrations, compliance, B-BBEE)
- Commercial contracts and document personalisation
- AI CV building and the NDLCS Education / UNISA LLB tutoring division
Do NOT write stories about medicine, romance, crime thrillers, or anything outside these services.

BRAND-SOLUTION ENDING — non-negotiable:
- The FINAL scene (id "end") MUST show how NDLCS solves the client's problem.
- It must name at least one concrete NDLCS service the client should use next,
  and include a clear call to action (visit nkadimengdannylcs.com, WhatsApp 071 276 6198, or book a consultation).
- The narrator delivers the closing CTA line.`;

    const systemPrompt = `You write SHORT, ENTERTAINING radio story scripts for the Nkadimeng Danny Legal & Compliance Support (NDLCS) story-video generator.

${scopeBlock}

OUTPUT FORMAT — follow EXACTLY, no markdown, no commentary, no code fences (the in-browser parser only understands this format; anything else is silently dropped and will NOT be spoken in the video):
- Each scene starts with a title line:  "<Scene title> @<primaryVoiceSlot> #<sceneId>"
- IMMEDIATELY AFTER the title, on their OWN lines, include BOTH cues for atmosphere:
    !venue=<courtroom|office|workshop|home|street|studio>
    !ambience=<same value>
  Rotate the venue across scenes so each scene has a DIFFERENT atmosphere where the story allows (e.g. start at home, move to office, then courtroom, then back to street). Do not reuse the same venue twice in a row unless the story strictly requires it.
- Dialogue lines:  "@<voiceSlot>: <what they say>"   (use the VOICE SLOT key, e.g. @narrator, @danny, @worker — NOT the alias)
- SPEAKER CLARITY — every dialogue line must make the speaker obvious in the audio: either the narrator names the speaker first (e.g. "@narrator: Thabo replied,") OR another character addresses them by their display label inside the line (e.g. "@danny: Thabo, listen carefully — ..."). The first time a character speaks in a scene, ensure their display label is spoken aloud by someone in that scene.
- Optional sfx cue on its own line: "!sfx=<key>"
- ${data.branching ? 'Branch choices on their own line: ">Choice label -> targetSceneId"' : "Do NOT include branch choices."}
- Separate scenes with a line containing ONLY:  ---
- Scene ids must be short, lowercase, no spaces (e.g. start, hearing, advice, end).
- Use ONLY the voice slots: narrator, danny, client, judge, worker, boss. Refer to characters by their DISPLAY LABEL inside the spoken text where natural.
- EVERY non-cue, non-choice line MUST be a dialogue turn prefixed with "@<voiceSlot>:" so it gets spoken. No prose paragraphs, no stage directions, no "[pause]" markers.
- The first scene id MUST be "start". The final scene id MUST be "end" and MUST contain the ${isLlb ? "NDLCS LLB tutoring invitation" : "NDLCS solution"} + CTA described above.
- Every choice's target MUST be the id of another scene in the script.
- If two or more characters are listed in the cast, AT LEAST TWO distinct voice slots MUST have spoken dialogue in the script (not only the narrator).

LENGTH — HARD CONSTRAINT. The finished video must run ${targetSeconds} seconds when spoken at ~140 words per minute.
- Total spoken words across ALL scenes MUST be between ${Math.round(wordBudget * 0.9)} and ${wordBudget}. Count them before you answer.
- Write EXACTLY ${sceneTarget} scenes, ~${perScene} spoken words each.
- Within each scene use 2 to 4 dialogue turns; individual lines 8–28 words.
- The story must still be COMPLETE inside this runtime: hook in scene 1, problem, turn, and the NDLCS solution + CTA in the final scene. Never trail off, never pad.${targetSeconds <= 45 ? "\n- At this length, cut all pleasantries: open on the problem in the first spoken line." : ""}


Tone: ${data.tone}.`;


    const userPrompt = `Topic: ${data.topic}

Characters (use the voice slot as the @key in every dialogue line):
${castLines}

${data.notes ? `Extra notes: ${data.notes}\n\n` : ""}Write the full script now, in the required format only. Remember: the final scene MUST end with NDLCS offering the solution and a clear call to action.`;

    const aiRes = await fetch(
      "https://ai.gateway.lovable.dev/v1/chat/completions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Lovable-API-Key": lovableKey,
        },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash",
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: userPrompt },
          ],
        }),
      },
    );
    if (!aiRes.ok) {
      const t = await aiRes.text().catch(() => "");
      if (aiRes.status === 429) throw new Error("Rate limit — try again in a moment.");
      if (aiRes.status === 402) throw new Error("Workspace AI credits exhausted.");
      throw new Error(`Script generation failed (${aiRes.status}): ${t.slice(0, 200)}`);
    }
    const aiJson = (await aiRes.json()) as {
      choices?: { message?: { content?: string } }[];
    };
    let script = (aiJson.choices?.[0]?.message?.content ?? "").trim();
    // Strip accidental code fences if the model added them.
    script = script.replace(/^```[a-z]*\n?/i, "").replace(/\n?```\s*$/i, "").trim();
    if (!script) throw new Error("Empty script returned by AI");

    // Defensive: if the model used a custom alias (e.g. @thabo) instead of the
    // voice slot (@worker), rewrite @alias → @slot so the parser/TTS recognise it.
    if (keyToSlot.size) {
      script = script.replace(/@([a-z][a-z0-9_]*)\b/gi, (m, k: string) => {
        const slot = keyToSlot.get(k.toLowerCase());
        return slot ? `@${slot}` : m;
      });
    }

    const spokenWords = (script.match(/^@[a-z]+:.*$/gim) ?? [])
      .join(" ")
      .split(/\s+/)
      .filter(Boolean).length;

    return {
      script,
      targetSeconds,
      sceneTarget,
      wordBudget,
      spokenWords,
      estimatedSeconds: Math.round((spokenWords / 140) * 60),
    };

  });
